import React, { useState, useEffect, useCallback } from 'react';
import OrbitLayout from '../components/OrbitLayout';
import { useLanguage } from '../context/LanguageContext';
import { useOrbitAuth } from '../context/OrbitAuthContext';
import { Search, Home, Briefcase, GraduationCap, ExternalLink, MapPin, Wifi, ChevronLeft, ChevronRight, Mail, Phone, MessageCircle, User } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const TYPE_CONFIG = {
  accommodation: { icon: Home, colorBg: 'bg-[var(--ls-terracotta)]/10', colorText: 'text-[var(--ls-terracotta)]', colorBorder: 'border-[var(--ls-terracotta)]/30' },
  service: { icon: Briefcase, colorBg: 'bg-[var(--ls-forest)]/10', colorText: 'text-[var(--ls-forest)]', colorBorder: 'border-[var(--ls-forest)]/30' },
  training: { icon: GraduationCap, colorBg: 'bg-[var(--ls-gold)]/10', colorText: 'text-[var(--ls-gold)]', colorBorder: 'border-[var(--ls-gold)]/30' },
};

const BrowseListingsPage = () => {
  const { language } = useLanguage();
  const { token } = useOrbitAuth();
  const t = (fr, en) => language === 'fr' ? fr : en;

  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [typeCounts, setTypeCounts] = useState({});

  // Filters
  const [activeType, setActiveType] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [remoteOnly, setRemoteOnly] = useState(false);
  const [searchInput, setSearchInput] = useState('');

  const headers = { Authorization: `Bearer ${token}` };

  const fetchListings = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: '12' });
      if (activeType) params.set('listing_type', activeType);
      if (searchQuery) params.set('search', searchQuery);
      if (cityFilter) params.set('city', cityFilter);
      if (remoteOnly) params.set('is_remote', 'true');

      const res = await fetch(`${API}/api/orbit/listings/browse?${params}`, { headers });
      if (res.ok) {
        const data = await res.json();
        setListings(data.listings || []);
        setTotal(data.total || 0);
        setPages(data.pages || 1);
        setTypeCounts(data.type_counts || {});
      }
    } catch (e) { console.error('Listings fetch error:', e); }
    finally { setLoading(false); }
  }, [token, page, activeType, searchQuery, cityFilter, remoteOnly]);

  useEffect(() => { fetchListings(); }, [fetchListings]);

  const handleSearch = (e) => {
    e.preventDefault();
    setSearchQuery(searchInput);
    setPage(1);
  };

  const handleTypeFilter = (type) => {
    setActiveType(type === activeType ? '' : type);
    setPage(1);
  };

  const typeLabels = {
    accommodation: t('Hebergement', 'Accommodation'),
    service: t('Service', 'Service'),
    training: t('Formation', 'Training'),
  };

  const totalAll = (typeCounts.accommodation || 0) + (typeCounts.service || 0) + (typeCounts.training || 0);

  const rankColors = {
    mercure: '#A0A0A0', venus: '#E8C060', terre: '#4A9B6E',
    mars: '#D05540', jupiter: '#D4943A', saturne: '#B8860B'
  };

  return (
    <OrbitLayout>
      <div className="space-y-6" data-testid="browse-listings-page">
        {/* Header */}
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-white">
            {t('Petites Annonces', 'Classifieds')}
          </h1>
          <p className="text-gray-300 mt-1">
            {t('Decouvrez les services, hebergements et formations offerts par les membres', 'Discover services, accommodations and training offered by members')}
          </p>
        </div>

        {/* Type Tabs */}
        <div className="flex flex-wrap gap-2" data-testid="type-filters">
          {[
            { key: '', label: t('Tout', 'All'), count: totalAll, color: 'bg-[var(--ls-bark)]' },
            { key: 'accommodation', label: typeLabels.accommodation, count: typeCounts.accommodation || 0, color: 'bg-[var(--ls-terracotta)]' },
            { key: 'service', label: typeLabels.service, count: typeCounts.service || 0, color: 'bg-[var(--ls-forest)]' },
            { key: 'training', label: typeLabels.training, count: typeCounts.training || 0, color: 'bg-[var(--ls-gold)]' },
          ].map(tab => (
            <button key={tab.key} onClick={() => handleTypeFilter(tab.key)}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-all ${
                activeType === tab.key
                  ? `${tab.color} text-white shadow-md`
                  : 'bg-white border border-[hsl(40,20%,88%)] text-[var(--ls-text-muted)] hover:bg-[var(--ls-sand-dark)]'
              }`}
              data-testid={`filter-${tab.key || 'all'}`}>
              {tab.label}
              <span className={`min-w-[20px] h-5 flex items-center justify-center px-1.5 rounded-full text-[10px] font-bold ${
                activeType === tab.key ? 'bg-white/20 text-white' : 'bg-[var(--ls-sand-dark)] text-[var(--ls-text-muted)]'
              }`}>{tab.count}</span>
            </button>
          ))}
        </div>

        {/* Search & Filters Bar */}
        <div className="flex flex-col sm:flex-row gap-3">
          <form onSubmit={handleSearch} className="flex-1 relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--ls-text-muted)]" />
            <input type="text" value={searchInput} onChange={e => setSearchInput(e.target.value)}
              placeholder={t('Rechercher une annonce...', 'Search listings...')}
              className="w-full pl-11 pr-4 py-2.5 rounded-xl border border-[hsl(40,20%,88%)] bg-white text-[var(--ls-text)] text-sm focus:outline-none focus:ring-2 focus:ring-[var(--ls-terracotta)]/20 focus:border-[var(--ls-terracotta)] transition-all"
              data-testid="search-input" />
          </form>
          <div className="flex gap-2">
            <input type="text" value={cityFilter} onChange={e => { setCityFilter(e.target.value); setPage(1); }}
              placeholder={t('Ville...', 'City...')}
              className="w-32 sm:w-40 px-4 py-2.5 rounded-xl border border-[hsl(40,20%,88%)] bg-white text-[var(--ls-text)] text-sm focus:outline-none focus:ring-2 focus:ring-[var(--ls-terracotta)]/20 focus:border-[var(--ls-terracotta)]"
              data-testid="city-filter" />
            <button onClick={() => { setRemoteOnly(!remoteOnly); setPage(1); }}
              className={`flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${
                remoteOnly ? 'bg-blue-50 border-2 border-blue-300 text-blue-700' : 'bg-white border border-[hsl(40,20%,88%)] text-[var(--ls-text-muted)]'
              }`}
              data-testid="remote-filter">
              <Wifi className="w-4 h-4" />
              {t('A distance', 'Remote')}
            </button>
          </div>
        </div>

        {/* Results */}
        {loading ? (
          <div className="flex justify-center py-16">
            <div className="w-8 h-8 border-3 border-[var(--ls-terracotta)] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : listings.length === 0 ? (
          <div className="text-center py-16 bg-white border border-[hsl(40,20%,88%)] rounded-2xl" data-testid="no-listings">
            <Search className="w-14 h-14 text-[var(--ls-text-muted)]/20 mx-auto mb-4" />
            <h3 className="font-heading font-bold text-[var(--ls-text)] mb-1">{t('Aucune annonce trouvee', 'No listings found')}</h3>
            <p className="text-sm text-[var(--ls-text-muted)]">
              {searchQuery || cityFilter || activeType || remoteOnly
                ? t('Essayez de modifier vos filtres', 'Try adjusting your filters')
                : t('Les membres n\'ont pas encore publie d\'annonces', 'Members haven\'t published any listings yet')}
            </p>
          </div>
        ) : (
          <>
            {/* Count */}
            <p className="text-sm text-[var(--ls-text-muted)]">
              {total} {t('annonce(s) trouvee(s)', 'listing(s) found')}
            </p>

            {/* Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="listings-grid">
              {listings.map(listing => {
                const cfg = TYPE_CONFIG[listing.listing_type] || TYPE_CONFIG.service;
                const Icon = cfg.icon;
                const title = language === 'fr' ? listing.title_fr : (listing.title_en || listing.title_fr);
                const desc = language === 'fr' ? listing.description_fr : (listing.description_en || listing.description_fr);
                const rankColor = rankColors[listing.owner_rank] || '#A0A0A0';

                return (
                  <div key={listing.listing_id}
                    className={`bg-white border ${cfg.colorBorder} rounded-2xl overflow-hidden hover:shadow-lg transition-all group`}
                    data-testid={`listing-card-${listing.listing_id}`}>
                    {/* Card Header */}
                    <div className={`${cfg.colorBg} px-5 py-3 flex items-center justify-between`}>
                      <div className="flex items-center gap-2">
                        <Icon className={`w-4 h-4 ${cfg.colorText}`} />
                        <span className={`text-xs font-bold ${cfg.colorText} uppercase tracking-wider`}>
                          {typeLabels[listing.listing_type]}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        {listing.is_remote && (
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-blue-50 text-blue-600 border border-blue-200">
                            <Wifi className="w-3 h-3 inline mr-0.5" />{t('A distance', 'Remote')}
                          </span>
                        )}
                        {listing.category && (
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-white/60 text-[var(--ls-text-muted)]">
                            {listing.category}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Card Body */}
                    <div className="p-5">
                      <h3 className="font-heading font-bold text-[var(--ls-text)] text-base mb-1.5 line-clamp-2 group-hover:text-[var(--ls-terracotta)] transition-colors">
                        {title}
                      </h3>
                      <p className="text-sm text-[var(--ls-text-muted)] line-clamp-3 mb-4">
                        {desc}
                      </p>

                      {/* Meta */}
                      <div className="flex flex-wrap items-center gap-2 mb-4">
                        {listing.price_indication && (
                          <span className="px-2.5 py-1 rounded-lg bg-[var(--ls-sand-dark)] text-[var(--ls-text)] text-xs font-bold">
                            {listing.price_indication}
                          </span>
                        )}
                        {(listing.city || listing.owner_city) && (
                          <span className="flex items-center gap-1 text-xs text-[var(--ls-text-muted)]">
                            <MapPin className="w-3 h-3" />{listing.city || listing.owner_city}
                          </span>
                        )}
                      </div>

                      {/* Owner */}
                      <div className="flex items-center justify-between pt-3 border-t border-[hsl(40,20%,90%)]">
                        <div className="flex items-center gap-2.5">
                          <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-xs"
                            style={{ backgroundColor: rankColor }}>
                            {listing.owner_picture
                              ? <img src={listing.owner_picture} alt="" className="w-8 h-8 rounded-full object-cover" />
                              : (listing.owner_name?.charAt(0)?.toUpperCase() || <User className="w-4 h-4" />)}
                          </div>
                          <div>
                            <p className="text-xs font-semibold text-[var(--ls-text)]">{listing.owner_name}</p>
                            {listing.owner_city && (
                              <p className="text-[10px] text-[var(--ls-text-muted)]">{listing.owner_city}</p>
                            )}
                          </div>
                        </div>

                        {/* Contact Actions */}
                        <div className="flex items-center gap-1">
                          {listing.external_link && (
                            <a href={listing.external_link} target="_blank" rel="noopener noreferrer"
                              className="p-2 rounded-lg hover:bg-[var(--ls-terracotta)]/10 text-[var(--ls-terracotta)] transition-colors"
                              title={t('Voir l\'annonce', 'View listing')} data-testid="listing-external-link">
                              <ExternalLink className="w-4 h-4" />
                            </a>
                          )}
                          {listing.owner_email && (
                            <a href={`mailto:${listing.owner_email}`}
                              className="p-2 rounded-lg hover:bg-[var(--ls-forest)]/10 text-[var(--ls-forest)] transition-colors"
                              title={t('Envoyer un courriel', 'Send email')}>
                              <Mail className="w-4 h-4" />
                            </a>
                          )}
                          {listing.owner_phone && (
                            <a href={`tel:${listing.owner_phone}`}
                              className="p-2 rounded-lg hover:bg-[var(--ls-gold)]/10 text-[var(--ls-gold)] transition-colors"
                              title={t('Appeler', 'Call')}>
                              <Phone className="w-4 h-4" />
                            </a>
                          )}
                          {listing.owner_messenger && (
                            <a href={listing.owner_messenger} target="_blank" rel="noopener noreferrer"
                              className="p-2 rounded-lg hover:bg-blue-50 text-blue-500 transition-colors"
                              title="Messenger">
                              <MessageCircle className="w-4 h-4" />
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Pagination */}
            {pages > 1 && (
              <div className="flex items-center justify-center gap-3 pt-4" data-testid="pagination">
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}
                  className="flex items-center gap-1 px-4 py-2 rounded-xl bg-white border border-[hsl(40,20%,88%)] text-sm font-medium text-[var(--ls-text)] hover:bg-[var(--ls-sand-dark)] disabled:opacity-40 transition-all">
                  <ChevronLeft className="w-4 h-4" />{t('Precedent', 'Previous')}
                </button>
                <span className="text-sm text-[var(--ls-text-muted)]">
                  {t('Page', 'Page')} {page} / {pages}
                </span>
                <button onClick={() => setPage(p => Math.min(pages, p + 1))} disabled={page >= pages}
                  className="flex items-center gap-1 px-4 py-2 rounded-xl bg-white border border-[hsl(40,20%,88%)] text-sm font-medium text-[var(--ls-text)] hover:bg-[var(--ls-sand-dark)] disabled:opacity-40 transition-all">
                  {t('Suivant', 'Next')}<ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </>
        )}

        {/* Disclaimer */}
        <div className="text-center text-[10px] text-[var(--ls-text-muted)] mt-4 px-4">
          {t(
            'Les transactions se font directement entre membres, en dehors de LocalSite.Club. La plateforme ne prend aucune commission et n\'agit pas comme intermediaire.',
            'Transactions happen directly between members, outside of LocalSite.Club. The platform takes no commission and does not act as an intermediary.'
          )}
        </div>
      </div>
    </OrbitLayout>
  );
};

export default BrowseListingsPage;
